import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import { useState, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocalStorage } from 'usehooks-ts'

import {
  Button,
  FormFieldText,
  FormFieldPassword,
  FormFieldCheckbox,
  ErrorMessageList,
} from '@/components/ui'
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/config'
import { LOCAL_STORAGE_KEY } from '@/const'
import { useLogin } from '@/hooks/features/auth'
import { invalidateUser } from '@/hooks/features/user'
import { storage } from '@/utils/storage'
import { getErrorMessages } from '@/utils/text'

import { schema, formLabels } from './const'
import { AuthLoginFormProps, LoginFormVO } from './type'
import { generateDefaultValues } from './util'

export const AuthLoginForm = ({
  onSuccess,
  onChangeErrorMessage,
}: AuthLoginFormProps) => {
  const { t } = useTranslation()
  const [errorMessages] = useState<string[]>([])

  const { isLoading, mutate } = useLogin({})
  const [defaultStoreCode, setDefaultStoreCode] = useLocalStorage(
    LOCAL_STORAGE_KEY.auth.storeCode,
    ''
  )

  const { register, control, formState, handleSubmit } = useForm<LoginFormVO>({
    resolver: schema && zodResolver(schema),
    shouldUnregister: true,
    defaultValues: generateDefaultValues(defaultStoreCode),
  })

  const onSubmit = useCallback(
    async ({ userId, password, storeCode, saveStoreCode }: LoginFormVO) => {
      onChangeErrorMessage([])
      mutate(
        { data: { userId, password, storeCode } },
        {
          onSuccess: async ({ accessToken, refreshToken }, variables) => {
            // Note: 次ログイン時に初期値として入力するために、localStorage に保存
            if (saveStoreCode) {
              setDefaultStoreCode(variables.data.storeCode)
            } else {
              window.localStorage.removeItem(LOCAL_STORAGE_KEY.auth.storeCode)
            }

            storage.set(ACCESS_TOKEN_KEY, accessToken)
            storage.set(REFRESH_TOKEN_KEY, refreshToken)
            // setAuth({ isLoggedIn: true })

            // Note: ユーザー情報更新
            await invalidateUser()

            onSuccess()
          },
          onError: (error) => {
            if (error.response?.status === 400) {
              onChangeErrorMessage(
                getErrorMessages({
                  fields: error.response.data.fields,
                  fieldNames: formLabels,
                })
              )
            }
            if (error.response?.status === 401) {
              onChangeErrorMessage(['ログインに失敗しました。'])
            }
          },
        }
      )
    },
    [onChangeErrorMessage, mutate, onSuccess, setDefaultStoreCode]
  )

  return (
    <div className={clsx('tw-w-full')}>
      {errorMessages.length > 0 && (
        <div className={clsx('tw-mb-2 tw-text-center')}>
          <ErrorMessageList errorMessages={errorMessages} />
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className={clsx('tw-mt-2')}>
        <FormFieldText
          type="text"
          label={t('auth.login.userId')}
          required
          error={formState.errors['userId']}
          registration={register('userId')}
        />
        <FormFieldPassword
          label={t('auth.login.password')}
          required
          name="password"
          error={formState.errors['password']}
          control={control}
        />
        <FormFieldText
          type="text"
          label={t('auth.login.storeCode')}
          required
          error={formState.errors['storeCode']}
          registration={register('storeCode')}
        />
        <FormFieldCheckbox
          name={'saveStoreCode'}
          label={t('auth.login.saveStoreCode')}
          error={formState.errors['saveStoreCode']}
          control={control}
        />
        <div className={clsx('tw-mt-12')}>
          <Button
            type="submit"
            isLoading={isLoading}
            className={clsx('tw-w-full')}
          >
            {t('auth.login.submit')}
          </Button>
        </div>
      </form>
    </div>
  )
}
